import { useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import { Paper } from '@mui/material';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import { useResponsive } from 'src/hooks/use-responsive';

import { useTranslate } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';
import { useEntityContext } from 'src/entity/hooks';

import Logo from 'src/components/logo';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import NavItem from 'src/components/nav-section/vertical/nav-item';
import NavSectionVertical from 'src/components/nav-section/vertical/nav-section-vertical';

import { NAV } from '../../config-layout';
import { useNavData } from './config-navigation';

// ----------------------------------------------------------------------

export default function NavVertical({ openNav, onCloseNav }) {
  const { t } = useTranslate();
  const { user } = useAuthContext();
  const pathname = usePathname();
  const lgUp = useResponsive('up', 'lg');
  const navData = useNavData();
  const { entity_id, parent_entity_id } = useEntityContext();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Paper
          sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          variant="outlined"
        >
          <Logo sx={{ width: '100%', height: 75, objectFit: 'contain' }} />
        </Paper>
      </Box>

      <NavSectionVertical
        data={navData}
        slotProps={{
          currentRole: user?.role,
        }}
      />
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ p: 2 }}>
        <NavItem
          title={t('common.back')}
          icon={<Iconify icon="mdi:arrow-left" />}
          path={paths.entity.modules.ges.dashboard.root(entity_id, parent_entity_id)}
        />
      </Box>
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: (theme) => theme.shadows[6],
            zIndex: (theme) => theme.zIndex.appBar + 1,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
          sx={{
            '& .MuiDrawer-paper': {
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderRight: (theme) => `solid 1px ${theme.palette.divider}`,
              boxShadow: (theme) => theme.shadows[6],
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

NavVertical.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};
